// src/screens/HistoryScreen.tsx - 編集・削除機能付き完全版
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Analytics,
  Paid,
  DeliveryDining,
  TrendingUp,
  AttachMoney,
  Schedule,
  PieChart,
  LocalShipping,
  List,
  Assignment,
  CalendarToday,
  DateRange,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Edit,
  Delete
} from '@mui/icons-material';
import { getAllDeliveries, deleteDelivery } from '../services/dataService';
import { DeliveryRecord, calculateActualWorkingMinutes } from '../types';
import AddDeliveryDialog from '../components/AddDeliveryDialog';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
  simpleMode?: boolean;
}

interface PlatformStat {
  platform: string;
  count: number;
  revenue: number;
  avgRevenue: number;
  totalMinutes: number;
}

function HistoryScreen({ onShowSnackbar, simpleMode = false }: Props) {
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedSpecificDate, setSelectedSpecificDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedDeliveryId, setExpandedDeliveryId] = useState<string | null>(null);
  
  // 編集・削除用
  const [editingDelivery, setEditingDelivery] = useState<DeliveryRecord | undefined>(undefined);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; delivery?: DeliveryRecord }>({ open: false });

  // カレンダー用の状態
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarYM, setCalendarYM] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth()
  });
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [rangeCalendarYM, setRangeCalendarYM] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth()
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await getAllDeliveries();
      setDeliveries(data);
    } catch (error) {
      onShowSnackbar('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const dateToString = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getDateRange = () => {
    const today = new Date();
    const todayStr = dateToString(today);

    switch (selectedPeriod) {
      case 'today':
        return { start: todayStr, end: todayStr };
      case 'week': {
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 6);
        return { start: dateToString(weekAgo), end: todayStr };
      }
      case 'month': {
        const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
        return { start: monthStart, end: todayStr };
      }
      case 'lastMonth': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: dateToString(lastMonth), end: dateToString(lastMonthEnd) };
      }
      default:
        return null;
    }
  };

  const filteredDeliveries = React.useMemo(() => {
    if (selectedPeriod === 'specific' && selectedSpecificDate) {
      const dateStr = dateToString(selectedSpecificDate);
      return deliveries.filter(d => d.date === dateStr);
    } else if (selectedPeriod === 'range' && selectedStartDate && selectedEndDate) {
      const startStr = dateToString(selectedStartDate);
      const endStr = dateToString(selectedEndDate);
      return deliveries.filter(d => d.date >= startStr && d.date <= endStr);
    } else if (selectedPeriod !== 'all') {
      const range = getDateRange();
      if (range) {
        return deliveries.filter(d => d.date >= range.start && d.date <= range.end);
      }
    }
    return deliveries;
  }, [deliveries, selectedPeriod, selectedSpecificDate, selectedStartDate, selectedEndDate]);

  const stats = React.useMemo(() => {
    const totalRevenue = filteredDeliveries.reduce((sum, d) => sum + d.totalRevenue, 0);
    const totalCount = filteredDeliveries.reduce((sum, d) => sum + d.deliveryCount, 0);
    
    // 日付ごとにグループ化して実働時間を計算
    const dateGroups = new Map<string, DeliveryRecord[]>();
    filteredDeliveries.forEach(d => {
      const group = dateGroups.get(d.date) || [];
      group.push(d);
      dateGroups.set(d.date, group);
    });
    
    // 各日の実働時間を合計
    const totalMinutes = Array.from(dateGroups.values())
      .reduce((sum, dayDeliveries) => sum + calculateActualWorkingMinutes(dayDeliveries), 0);
    
    const avgHourlyRate = totalMinutes > 0 ? Math.floor((totalRevenue / totalMinutes) * 60) : 0;
    const avgRevenue = totalCount > 0 ? Math.floor(totalRevenue / totalCount) : 0;

    return { totalRevenue, totalCount, avgHourlyRate, avgRevenue, totalMinutes };
  }, [filteredDeliveries]);

  const platformStats = React.useMemo(() => {
    const platformMap = new Map<string, PlatformStat>();
    
    // プラットフォームごとの売上・件数・稼働時間を集計
    filteredDeliveries.forEach(d => {
      const platform = d.platform || '未設定';
      const current = platformMap.get(platform) || { 
        platform, 
        count: 0, 
        revenue: 0, 
        avgRevenue: 0, 
        totalMinutes: 0 
      };
      
      platformMap.set(platform, {
        platform,
        count: current.count + d.deliveryCount,
        revenue: current.revenue + d.totalRevenue,
        avgRevenue: 0,
        totalMinutes: current.totalMinutes + calculateActualWorkingMinutes([d])
      });
    });

    return Array.from(platformMap.values())
      .map(stat => ({
        ...stat,
        avgRevenue: stat.count > 0 ? Math.floor(stat.revenue / stat.count) : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [filteredDeliveries]);

  // 編集ハンドラー
  const handleEdit = (delivery: DeliveryRecord) => {
    setEditingDelivery(delivery);
    setShowEditDialog(true);
  };

  // 削除ハンドラー
  const handleDelete = (delivery: DeliveryRecord) => {
    setDeleteDialog({ open: true, delivery });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.delivery?.id) return;

    try {
      await deleteDelivery(deleteDialog.delivery.id);
      await loadData();
      onShowSnackbar('記録を削除しました', 'success');
      setDeleteDialog({ open: false });
    } catch (error) {
      onShowSnackbar('削除に失敗しました', 'error');
    }
  };

  const formatCurrency = (amount: number) => `¥${amount.toLocaleString()}`;

  const periodLabel = useMemo(() => {
    if (selectedPeriod === 'today') return '今日';
    if (selectedPeriod === 'week') return '今週';
    if (selectedPeriod === 'month') return '今月';
    if (selectedPeriod === 'lastMonth') return '先月';
    if (selectedPeriod === 'specific' && selectedSpecificDate) {
      return selectedSpecificDate.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' });
    }
    if (selectedPeriod === 'range' && selectedStartDate && selectedEndDate) {
      return `${selectedStartDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })} - ${selectedEndDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}`;
    }
    return '全期間';
  }, [selectedPeriod, selectedSpecificDate, selectedStartDate, selectedEndDate]);

  // カレンダー用の計算
  const daysInMonth = useMemo(
    () => new Date(calendarYM.year, calendarYM.month + 1, 0).getDate(),
    [calendarYM]
  );
  const firstDayOfWeek = useMemo(
    () => new Date(calendarYM.year, calendarYM.month, 1).getDay(),
    [calendarYM]
  );

  const rangeDaysInMonth = useMemo(
    () => new Date(rangeCalendarYM.year, rangeCalendarYM.month + 1, 0).getDate(),
    [rangeCalendarYM]
  );
  const rangeFirstDayOfWeek = useMemo(
    () => new Date(rangeCalendarYM.year, rangeCalendarYM.month, 1).getDay(),
    [rangeCalendarYM]
  );

  const prevMonth = () => {
    if (calendarYM.month === 0) {
      setCalendarYM({ year: calendarYM.year - 1, month: 11 });
    } else {
      setCalendarYM({ year: calendarYM.year, month: calendarYM.month - 1 });
    }
  };

  const nextMonth = () => {
    if (calendarYM.month === 11) {
      setCalendarYM({ year: calendarYM.year + 1, month: 0 });
    } else {
      setCalendarYM({ year: calendarYM.year, month: calendarYM.month + 1 });
    }
  };

  const rangePrevMonth = () => {
    if (rangeCalendarYM.month === 0) {
      setRangeCalendarYM({ year: rangeCalendarYM.year - 1, month: 11 });
    } else {
      setRangeCalendarYM({ year: rangeCalendarYM.year, month: rangeCalendarYM.month - 1 });
    }
  };

  const rangeNextMonth = () => {
    if (rangeCalendarYM.month === 11) {
      setRangeCalendarYM({ year: rangeCalendarYM.year + 1, month: 0 });
    } else {
      setRangeCalendarYM({ year: rangeCalendarYM.year, month: rangeCalendarYM.month + 1 });
    }
  };

  const isSameDay = (d1: Date, d2: Date) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  const isInRange = (date: Date) => {
    if (!rangeStart || !rangeEnd) return false;
    const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
    const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
    return date >= start && date <= end;
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="white">読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0b0f0d', minHeight: '100vh', pb: 10 }}>
      <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
        <Typography fontSize={15} color="#8a948d" fontWeight="medium">
          履歴
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
          <Typography fontSize={24} fontWeight={800} color="white">
            配達記録
          </Typography>
          <Chip
            label={periodLabel}
            size="small"
            sx={{ bgcolor: 'rgba(16, 185, 129, 0.14)', color: '#34D399', fontWeight: 700 }}
          />
        </Box>
      </Box>

      {/* 期間選択 */}
      <Card sx={{ m: 2, bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Assignment sx={{ fontSize: 20, color: '#10B981', mr: 1 }} />
            <Typography fontSize={17} fontWeight="bold" color="white">
              期間選択
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 1 }}>
            {['today', 'week', 'month'].map((period) => (
              <Chip
                key={period}
                label={period === 'today' ? '今日' : period === 'week' ? '今週' : '今月'}
                onClick={() => setSelectedPeriod(period)}
                sx={{
                  bgcolor: selectedPeriod === period ? '#10B981' : '#2a2a2a',
                  color: selectedPeriod === period ? '#fff' : '#888',
                  fontWeight: selectedPeriod === period ? 'bold' : 'normal',
                  '&:hover': {
                    bgcolor: selectedPeriod === period ? '#0d9668' : '#333'
                  }
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            <Chip
              label="全期間"
              onClick={() => setSelectedPeriod('all')}
              sx={{
                bgcolor: selectedPeriod === 'all' ? '#10B981' : '#2a2a2a',
                color: selectedPeriod === 'all' ? '#fff' : '#888',
                fontWeight: selectedPeriod === 'all' ? 'bold' : 'normal',
                '&:hover': {
                  bgcolor: selectedPeriod === 'all' ? '#0d9668' : '#333'
                }
              }}
            />

            <Button
              fullWidth
              variant="outlined"
              startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
              onClick={() => setShowDatePicker(true)}
              sx={{
                borderColor: selectedPeriod === 'specific' ? '#10B981' : '#333',
                color: selectedPeriod === 'specific' ? '#10B981' : '#888',
                fontSize: 13,
                py: 1,
                '&:hover': {
                  borderColor: selectedPeriod === 'specific' ? '#10B981' : '#444',
                  bgcolor: 'transparent'
                }
              }}
            >
              {selectedPeriod === 'specific' && selectedSpecificDate
                ? selectedSpecificDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
                : '特定日'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<DateRange sx={{ fontSize: 16 }} />}
              onClick={() => setShowDateRangePicker(true)}
              sx={{
                borderColor: selectedPeriod === 'range' ? '#10B981' : '#333',
                color: selectedPeriod === 'range' ? '#10B981' : '#888',
                fontSize: 13,
                py: 1,
                '&:hover': {
                  borderColor: selectedPeriod === 'range' ? '#10B981' : '#444',
                  bgcolor: 'transparent'
                }
              }}
            >
              {selectedPeriod === 'range' && selectedStartDate && selectedEndDate
                ? `${selectedStartDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}-${selectedEndDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}`
                : '期間指定'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* サマリー統計 */}
      <Box sx={{ px: 2 }}>
        <Box sx={{ mb: 1.5 }}>
          <Card sx={{ bgcolor: '#102118', border: '1px solid #214332', borderRadius: '12px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Analytics sx={{ fontSize: 20, color: '#34D399', mr: 1 }} />
                  <Typography fontSize={17} fontWeight="bold" color="white">
                    統計サマリー
                  </Typography>
                </Box>
                <Typography fontSize={14} color="#9ca7a0">
                  {filteredDeliveries.length}記録
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25, mb: 1.25 }}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 1.5 }}>
                    <Paid sx={{ fontSize: 18, color: '#10B981', mb: 0.75 }} />
                    <Typography fontSize={18} fontWeight="bold" color="#10B981" noWrap>
                      {formatCurrency(stats.totalRevenue)}
                    </Typography>
                    <Typography fontSize={13} color="#ccc">総売上</Typography>
                </Box>

                <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 1.5 }}>
                    <DeliveryDining sx={{ fontSize: 18, color: '#3B82F6', mb: 0.75 }} />
                    <Typography fontSize={18} fontWeight="bold" color="#3B82F6" noWrap>
                      {stats.totalCount}件
                    </Typography>
                    <Typography fontSize={13} color="#ccc">総配達数</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: simpleMode ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
                {!simpleMode && (
                  <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 1.5 }}>
                      <TrendingUp sx={{ fontSize: 18, color: '#8B5CF6', mb: 0.75 }} />
                      <Typography fontSize={17} fontWeight="bold" color="#8B5CF6" noWrap>
                        {formatCurrency(stats.avgHourlyRate)}
                      </Typography>
                      <Typography fontSize={13} color="#ccc">平均時給</Typography>
                  </Box>
                )}
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 1.5 }}>
                    <AttachMoney sx={{ fontSize: 18, color: '#F59E0B', mb: 0.75 }} />
                    <Typography fontSize={17} fontWeight="bold" color="#F59E0B" noWrap>
                      {formatCurrency(stats.avgRevenue)}
                    </Typography>
                    <Typography fontSize={13} color="#ccc">平均単価</Typography>
                </Box>
              </Box>

              {!simpleMode && (
                <>
                  <Box sx={{ borderTop: '1px solid #2a4a3a', my: 1.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Schedule sx={{ fontSize: 16, color: '#888' }} />
                      <Typography fontSize={15} color="#888">
                        総稼働時間
                      </Typography>
                    </Box>
                    <Typography fontSize={16} fontWeight="medium" color="#10B981">
                      {Math.floor(stats.totalMinutes / 60)}時間{stats.totalMinutes % 60}分
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* プラットフォーム別統計 */}
        {!simpleMode && platformStats.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <PieChart sx={{ fontSize: 18, color: '#3B82F6', mr: 1 }} />
                  <Typography fontSize={16} fontWeight="bold" color="white">
                    サービス別統計
                  </Typography>
                </Box>

                {platformStats.map((stat, index) => {
                  const percentage = stats.totalRevenue > 0 
                    ? Math.round((stat.revenue / stats.totalRevenue) * 100)
                    : 0;
                  const avgHourlyRate = stat.totalMinutes > 0
                    ? Math.floor((stat.revenue / stat.totalMinutes) * 60)
                    : 0;

                  return (
                    <Box key={index} sx={{ mb: index < platformStats.length - 1 ? 1.5 : 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '5px', bgcolor: '#3B82F6' }} />
                          <Typography fontSize={16} fontWeight="bold" color="white">
                            {stat.platform}
                          </Typography>
                        </Box>
                        <Typography fontSize={17} fontWeight="bold" color="#3B82F6">
                          {formatCurrency(stat.revenue)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', borderRadius: '6px', px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocalShipping sx={{ fontSize: 12, color: '#10B981' }} />
                            <Typography fontSize={13} fontWeight="medium" color="#10B981">
                              {stat.count}件
                            </Typography>
                        </Box>

                        <Box sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', borderRadius: '6px', px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachMoney sx={{ fontSize: 12, color: '#F59E0B' }} />
                            <Typography fontSize={13} fontWeight="medium" color="#F59E0B">
                              平均{formatCurrency(stat.avgRevenue)}
                            </Typography>
                        </Box>

                        <Box sx={{ bgcolor: 'rgba(139, 92, 246, 0.15)', borderRadius: '6px', px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TrendingUp sx={{ fontSize: 12, color: '#8B5CF6' }} />
                            <Typography fontSize={13} fontWeight="medium" color="#8B5CF6">
                              時給{formatCurrency(avgHourlyRate)}
                            </Typography>
                        </Box>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: '#2a2a2a',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#3B82F6',
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* 配達履歴リスト */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <List sx={{ fontSize: 20, color: '#fff', mr: 1 }} />
            <Typography fontSize={17} fontWeight="bold" color="white">
              配達履歴
            </Typography>
            </Box>
            <Typography fontSize={14} color="#8a948d">
              {filteredDeliveries.length}件
            </Typography>
          </Box>

          {filteredDeliveries.length === 0 ? (
            <Card sx={{ bgcolor: '#1a1a1a', borderRadius: '12px', p: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Assignment sx={{ fontSize: 48, color: '#444', mb: 2 }} />
                <Typography fontSize={16} color="#666">
                  データがありません
                </Typography>
              </Box>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredDeliveries.map((delivery, index) => {
                const deliveryKey = delivery.id || `${delivery.date}-${delivery.platform}-${index}`;
                const isExpanded = expandedDeliveryId === deliveryKey;
                const deliveryWorkingMinutes = calculateActualWorkingMinutes([delivery]);

                return (
                <Card key={deliveryKey} sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography fontSize={16} fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
                          {new Date(delivery.date).toLocaleDateString('ja-JP', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            weekday: 'short'
                          })}
                        </Typography>
                        <Chip
                          label={delivery.platform}
                          size="small"
                          sx={{
                            bgcolor: '#2a2a2a',
                            color: '#10B981',
                            fontSize: 12,
                            height: 24
                          }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedDeliveryId(isExpanded ? null : deliveryKey)}
                        sx={{
                          color: '#9ca7a0',
                          bgcolor: 'rgba(255,255,255,0.06)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                      >
                        {isExpanded ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography fontSize={24} fontWeight="bold" color="#10B981">
                        {formatCurrency(delivery.totalRevenue)}
                      </Typography>
                      <Typography fontSize={16} color="#888">
                        {delivery.deliveryCount}件配達
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      size="small"
                      onClick={() => setExpandedDeliveryId(isExpanded ? null : deliveryKey)}
                      sx={{
                        justifyContent: 'space-between',
                        color: '#9ca7a0',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        borderRadius: '8px',
                        px: 1.25,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' }
                      }}
                      endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                    >
                      詳細
                    </Button>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #26312b' }}>
                        {!simpleMode && delivery.startTime && delivery.endTime ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Chip
                              size="small"
                              icon={<Schedule sx={{ fontSize: 14 }} />}
                              label={`${delivery.startTime} - ${delivery.endTime}`}
                              sx={{ bgcolor: 'rgba(59, 130, 246, 0.14)', color: '#93C5FD' }}
                            />
                            {delivery.breakMinutes > 0 && (
                              <Chip
                                size="small"
                                label={`休憩 ${delivery.breakMinutes}分`}
                                sx={{ bgcolor: 'rgba(245, 158, 11, 0.14)', color: '#FBBF24' }}
                              />
                            )}
                            {deliveryWorkingMinutes > 0 && (
                              <Chip
                                size="small"
                                label={`稼働 ${Math.floor(deliveryWorkingMinutes / 60)}時間${deliveryWorkingMinutes % 60}分`}
                                sx={{ bgcolor: 'rgba(16, 185, 129, 0.14)', color: '#6EE7B7' }}
                              />
                            )}
                          </Box>
                        ) : !simpleMode ? (
                          <Typography fontSize={14} color="#666" sx={{ mb: 1 }}>
                            稼働時間は未入力です
                          </Typography>
                        ) : null}

                        {delivery.notes && (
                          <Typography fontSize={14} color="#a7b0aa" sx={{ mb: 1.25, whiteSpace: 'pre-wrap' }}>
                            {delivery.notes}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Edit sx={{ fontSize: 18 }} />}
                            onClick={() => handleEdit(delivery)}
                            sx={{ borderColor: '#2d5b83', color: '#93C5FD' }}
                          >
                            編集
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Delete sx={{ fontSize: 18 }} />}
                            onClick={() => handleDelete(delivery)}
                            sx={{ borderColor: '#6b2c2c', color: '#F87171' }}
                          >
                            削除
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        PaperProps={{
          sx: {
            bgcolor: '#1a1a1a',
            color: 'white',
            borderRadius: '16px',
            m: 2
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          記録を削除しますか?
        </DialogTitle>
        <DialogContent>
          <Typography color="#888">
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setDeleteDialog({ open: false })}
            sx={{ 
              borderColor: '#444', 
              color: '#888',
              '&:hover': { borderColor: '#666', bgcolor: 'transparent' }
            }}
          >
            キャンセル
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={confirmDelete}
            sx={{ 
              bgcolor: '#EF4444', 
              '&:hover': { bgcolor: '#dc2626' }
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <AddDeliveryDialog
        open={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setEditingDelivery(undefined);
        }}
        onSuccess={() => {
          loadData();
          setShowEditDialog(false);
          setEditingDelivery(undefined);
        }}
        onShowSnackbar={onShowSnackbar}
        editData={editingDelivery}
      />

      {/* 特定日選択ダイアログ */}
      <Dialog
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1a1a1a',
            color: 'white',
            borderRadius: '16px',
            m: 2,
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          日付を選択
        </DialogTitle>
        <DialogContent>
          {/* カレンダーヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={prevMonth} sx={{ color: 'white' }}>
              <ChevronLeft />
            </IconButton>
            <Typography fontSize={17} fontWeight="bold">
              {calendarYM.year}年 {calendarYM.month + 1}月
            </Typography>
            <IconButton onClick={nextMonth} sx={{ color: 'white' }}>
              <ChevronRight />
            </IconButton>
          </Box>

          {/* 曜日ヘッダー */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: 'center',
                  py: 1,
                  color: i === 0 ? '#EF4444' : i === 6 ? '#3B82F6' : '#888',
                  fontWeight: 'bold',
                  fontSize: 12
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* カレンダー本体 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {Array.from({ length: firstDayOfWeek }, (_, i) => (
              <Box key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(calendarYM.year, calendarYM.month, day);
              const isSelected = calendarDate && isSameDay(date, calendarDate);
              
              return (
                <Box
                  key={day}
                  onClick={() => {
                    setCalendarDate(date);
                    setSelectedSpecificDate(date);
                    setSelectedPeriod('specific');
                    setShowDatePicker(false);
                  }}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    bgcolor: isSelected ? '#10B981' : 'transparent',
                    color: isSelected ? '#fff' : '#fff',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: 14,
                    '&:hover': {
                      bgcolor: isSelected ? '#0d9668' : '#2a2a2a'
                    }
                  }}
                >
                  {day}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {/* 期間選択ダイアログ */}
      <Dialog
        open={showDateRangePicker}
        onClose={() => setShowDateRangePicker(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1a1a1a',
            color: 'white',
            borderRadius: '16px',
            m: 2,
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          {selectingStart ? '開始日を選択' : '終了日を選択'}
        </DialogTitle>
        <DialogContent>
          {/* カレンダーヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={rangePrevMonth} sx={{ color: 'white' }}>
              <ChevronLeft />
            </IconButton>
            <Typography fontSize={17} fontWeight="bold">
              {rangeCalendarYM.year}年 {rangeCalendarYM.month + 1}月
            </Typography>
            <IconButton onClick={rangeNextMonth} sx={{ color: 'white' }}>
              <ChevronRight />
            </IconButton>
          </Box>

          {/* 曜日ヘッダー */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: 'center',
                  py: 1,
                  color: i === 0 ? '#EF4444' : i === 6 ? '#3B82F6' : '#888',
                  fontWeight: 'bold',
                  fontSize: 12
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* カレンダー本体 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {Array.from({ length: rangeFirstDayOfWeek }, (_, i) => (
              <Box key={`empty-${i}`} />
            ))}
            {Array.from({ length: rangeDaysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(rangeCalendarYM.year, rangeCalendarYM.month, day);
              const isStart = rangeStart && isSameDay(date, rangeStart);
              const isEnd = rangeEnd && isSameDay(date, rangeEnd);
              const inRange = isInRange(date);
              
              return (
                <Box
                  key={day}
                  onClick={() => {
                    if (selectingStart) {
                      setRangeStart(date);
                      setRangeEnd(null);
                      setSelectingStart(false);
                    } else {
                      setRangeEnd(date);
                      setSelectingStart(true);
                    }
                  }}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    bgcolor: (isStart || isEnd) ? '#10B981' : inRange ? '#0a2318' : 'transparent',
                    color: (isStart || isEnd) ? '#fff' : '#fff',
                    fontWeight: (isStart || isEnd) ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: 14,
                    '&:hover': {
                      bgcolor: (isStart || isEnd) ? '#0d9668' : inRange ? '#0a1e15' : '#2a2a2a'
                    }
                  }}
                >
                  {day}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setRangeStart(null);
                setRangeEnd(null);
                setSelectingStart(true);
              }}
              sx={{
                borderColor: '#444',
                color: '#888',
                '&:hover': { borderColor: '#666', bgcolor: 'transparent' }
              }}
            >
              リセット
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!rangeStart || !rangeEnd}
              onClick={() => {
                if (rangeStart && rangeEnd) {
                  const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
                  const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
                  setSelectedStartDate(start);
                  setSelectedEndDate(end);
                  setSelectedPeriod('range');
                  setShowDateRangePicker(false);
                }
              }}
              sx={{
                bgcolor: rangeStart && rangeEnd ? '#10B981' : '#444',
                color: rangeStart && rangeEnd ? '#fff' : '#666',
                fontWeight: 'bold',
                '&:hover': { bgcolor: rangeStart && rangeEnd ? '#0d9668' : '#444' },
                '&:disabled': { bgcolor: '#444', color: '#666' }
              }}
            >
              決定
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default HistoryScreen;

